/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import React from 'react';
import {translate} from '@ion/core/src/locale/locale';
import {Entity} from '@ion/core/src/types/entities';
import {IonSnackbar} from '../notifications/IonSnackbar';

export type ExportPromptType = {
  entity: Entity | string;
  isExporting: boolean;
  isFailure: boolean;
  onComplete: () => void;
  autoHideDuration?: number;
  message?: string;
}

/**
 * Provides a utility which determines the export message to be displayed.
 */
const getMessage = (isExporting: boolean, isFailure: boolean, entity: Entity | string): string => {
  if (!isFailure) {
    return translate(`common:export.${isExporting ? 'exporting' : 'completed'}`, {
      entity : translate(`common:entityLabels.${entity}`)
    });
  }
  return translate('common:export.failed', {entity});
};

/**
 * @ExportPrompt
 *
 * The Export Prompt displays the various states of a Bulk Export operations; generally,
 * via a Grid Export.
 *
 * ```typescript
 *
 * import {ExportPrompt} from '@ion/dls/src/export/ExportPrompt';
 *
 * <ExportPrompt entity={Entity.asset}
 *   isFailure={false}
 *   isExporting={false}
 *   onComplete={onComplete}
 *   downloader={downloader} />
 * ```
 *
 * @param {Entity | string} entity entity type that is being deleted
 * @param {Function} onComplete callback invoked when the user clicks ok.
 */
export const ExportPrompt = ({
  entity,
  isExporting,
  isFailure,
  onComplete,
  message,
  autoHideDuration = 4000
}: ExportPromptType) => {
  return (
    <IonSnackbar message={message || getMessage(isExporting, isFailure, entity)}
      autoHideDuration={autoHideDuration}
      disableWindowBlurListener
      disableClickAwayListener
      onClose={function() {
        onComplete();
      }}
      show />
  );
};

export default ExportPrompt;
